import { tr } from '@/lib/i18n-core';
import { memo, useDeferredValue, useEffect, useMemo, useRef, useState, type ReactNode, type RefObject } from 'react';
import { ChevronDown, ChevronRight, ChevronUp, ChevronsDownUp, ChevronsUpDown, Copy } from 'lucide-react';
import { cn, copyText } from '@/lib/utils';
import { Button, Input } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { ROOT_ID, defaultExpanded, expandAllIds, indexOfRow, matchOrder, searchTree, toJsonText, visibleRows, windowOf, type TreeRow } from './json-tree';

const ROW_HEIGHT = 22;
const VIEWPORT_HEIGHT = 420;
const MIN_QUERY = 2;
const RAW_LINE_LIMIT = 2000;

const KIND_CLASS: Record<TreeRow['kind'], string> = {
  object: 'text-muted-foreground',
  array: 'text-muted-foreground',
  string: 'text-success',
  number: 'text-info',
  boolean: 'text-warning',
  null: 'text-muted-foreground italic',
};

function VirtualList({ count, scrollRef, renderRow }: { count: number; scrollRef: RefObject<HTMLDivElement>; renderRow: (index: number) => ReactNode }) {
  const [scrollTop, setScrollTop] = useState(0);
  const { start, end } = windowOf(count, scrollTop, VIEWPORT_HEIGHT, ROW_HEIGHT);
  const rows: ReactNode[] = [];
  for (let index = start; index < end; index += 1) rows.push(renderRow(index));
  return (
    <div
      ref={scrollRef}
      dir="ltr"
      className="overflow-auto rounded-md border bg-background font-mono text-xs"
      style={{ height: VIEWPORT_HEIGHT }}
      onScroll={(event) => setScrollTop(event.currentTarget.scrollTop)}
    >
      <div className="relative min-w-max" style={{ height: count * ROW_HEIGHT }}>
        <div className="absolute inset-x-0" style={{ top: start * ROW_HEIGHT }}>
          {rows}
        </div>
      </div>
    </div>
  );
}

const TreeLine = memo(function TreeLine({ row, matched, current, onToggle, onCopy }: { row: TreeRow; matched: boolean; current: boolean; onToggle: (id: string) => void; onCopy: (value: unknown) => void }) {
  return (
    <div
      className={cn('group flex items-center gap-1 whitespace-nowrap pe-3 hover:bg-muted/60', matched && 'bg-warning/15', current && 'bg-warning/30')}
      style={{ height: ROW_HEIGHT, paddingInlineStart: 6 + row.depth * 14 }}
    >
      {row.expandable ? (
        <button type="button" className="shrink-0 text-muted-foreground focus-ring" aria-expanded={row.expanded} aria-label={row.label} onClick={() => onToggle(row.id)}>
          {row.expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        </button>
      ) : (
        <span className="inline-block h-3.5 w-3.5 shrink-0" />
      )}
      <span className="font-semibold text-foreground">{row.label}</span>
      <span className="text-muted-foreground">:</span>
      <span className={KIND_CLASS[row.kind]}>{row.preview}</span>
      <button type="button" className="ms-2 hidden shrink-0 text-muted-foreground hover:text-foreground focus-ring group-hover:inline-flex" aria-label={tr('billing', 'nx_copy_value')} onClick={() => onCopy(row.value)}>
        <Copy className="h-3 w-3" />
      </button>
    </div>
  );
});

function RawLines({ text }: { text: string }) {
  const lines = useMemo(() => text.split('\n'), [text]);
  const scrollRef = useRef<HTMLDivElement>(null);
  return (
    <VirtualList
      count={lines.length}
      scrollRef={scrollRef}
      renderRow={(index) => {
        const line = lines[index] ?? '';
        return (
          <div key={index} className="whitespace-pre px-2" style={{ height: ROW_HEIGHT }}>
            {line.length > RAW_LINE_LIMIT ? `${line.slice(0, RAW_LINE_LIMIT)}…` : line}
          </div>
        );
      }}
    />
  );
}

export function JsonViewer({ body, rootLabel }: { body: unknown; rootLabel?: string }) {
  const toast = useToast();
  const [expanded, setExpanded] = useState<Set<string>>(() => defaultExpanded(body));
  const [query, setQuery] = useState('');
  const [raw, setRaw] = useState(false);
  const [cursor, setCursor] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const deferred = useDeferredValue(query);

  useEffect(() => {
    setExpanded(defaultExpanded(body));
    setQuery('');
    setCursor(0);
  }, [body]);

  const search = useMemo(() => (deferred.trim().length >= MIN_QUERY ? searchTree(body, deferred) : null), [body, deferred]);
  const effective = useMemo(() => {
    if (!search || search.reveal.size === 0) return expanded;
    return new Set([...expanded, ...search.reveal]);
  }, [expanded, search]);
  const rows = useMemo(() => (raw ? [] : visibleRows(body, effective, rootLabel ?? tr('billing', 'nx_message'))), [body, effective, raw, rootLabel]);
  const matchSet = useMemo(() => new Set(search?.matches ?? []), [search]);
  const ordered = useMemo(() => (search ? matchOrder(rows, matchSet) : []), [rows, matchSet, search]);
  const rawText = useMemo(() => (raw ? toJsonText(body) : ''), [raw, body]);
  const currentId = ordered[Math.min(cursor, Math.max(0, ordered.length - 1))] ?? null;

  useEffect(() => {
    setCursor(0);
  }, [deferred]);

  useEffect(() => {
    if (!currentId || !scrollRef.current) return;
    const index = indexOfRow(rows, currentId);
    if (index >= 0) scrollRef.current.scrollTop = Math.max(0, index * ROW_HEIGHT - VIEWPORT_HEIGHT / 2);
  }, [currentId, rows]);

  const toggle = (id: string) =>
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const copy = async (value: unknown) => {
    const done = await copyText(typeof value === 'string' ? value : toJsonText(value));
    if (done) toast.success(tr('billing', 'nx_copied'));
    else toast.error(tr('billing', 'nx_could_not_copy'));
  };

  const step = (direction: 1 | -1) => {
    if (ordered.length === 0) return;
    setCursor((current) => (current + direction + ordered.length) % ordered.length);
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        {!raw && (
          <>
            <Input className="h-8 w-48" value={query} placeholder={tr('billing', 'nx_search_in_message')} aria-label={tr('billing', 'nx_search_in_message')} onChange={(event) => setQuery(event.target.value)} onKeyDown={(event) => event.key === 'Enter' && step(event.shiftKey ? -1 : 1)} />
            {search && (
              <>
                <span className="text-xs text-muted-foreground">
                  {search.matches.length === 0 ? tr('billing', 'nx_no_matches') : tr('billing', 'nx_match_x_of_y', { current: Math.min(cursor, ordered.length - 1) + 1, total: search.truncated ? `${search.matches.length}+` : search.matches.length })}
                </span>
                <Button size="icon" variant="ghost" aria-label={tr('billing', 'nx_previous_match')} disabled={ordered.length === 0} onClick={() => step(-1)}>
                  <ChevronUp className="h-4 w-4" />
                </Button>
                <Button size="icon" variant="ghost" aria-label={tr('billing', 'nx_next_match')} disabled={ordered.length === 0} onClick={() => step(1)}>
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </>
            )}
            <Button size="sm" variant="outline" className="gap-1" onClick={() => setExpanded(expandAllIds(body))}>
              <ChevronsUpDown className="h-3.5 w-3.5" /> {tr('billing', 'nx_expand_all')}
            </Button>
            <Button size="sm" variant="outline" className="gap-1" onClick={() => setExpanded(new Set([ROOT_ID]))}>
              <ChevronsDownUp className="h-3.5 w-3.5" /> {tr('billing', 'nx_collapse_all')}
            </Button>
          </>
        )}
        <Button size="sm" variant="outline" className="gap-1" onClick={() => void copy(body)}>
          <Copy className="h-3.5 w-3.5" /> {tr('billing', 'nx_copy')}
        </Button>
        <label className="ms-auto inline-flex items-center gap-1.5 text-xs">
          <input type="checkbox" checked={raw} onChange={(event) => setRaw(event.target.checked)} />
          {tr('billing', 'nx_show_raw')}
        </label>
      </div>
      {raw ? (
        <RawLines text={rawText} />
      ) : (
        <VirtualList
          count={rows.length}
          scrollRef={scrollRef}
          renderRow={(index) => {
            const row = rows[index] as TreeRow;
            return <TreeLine key={row.id} row={row} matched={matchSet.has(row.id)} current={row.id === currentId} onToggle={toggle} onCopy={(value) => void copy(value)} />;
          }}
        />
      )}
    </div>
  );
}
